import { useMemo, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { Avatar, ButtonProps, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import {
  GridColDef,
  GridRowParams,
  GridValueGetterParams,
} from '@mui/x-data-grid';
import { DataGrid } from '@/components';
import PageHeader from '@/components/PageHeader';
import ContentTabs from './Tabs';
import ContentDrawer from './Drawer';
import { useFetchContentQuery } from '@/features/organizations/api/contentApi';
import { useAccessControl } from '@/features/access-control';
import { useAppSelector } from '@/hooks';
import OfferDrawer from './offers/OfferDrawer';
import ButtonGroup from '@/components/ButtonGroup';
import { Content, ContentStatus, Offer } from '@/types';
import routes from '@/constants/routes';
import { getInitials } from '@/utils/names';
import { lightGreen, red } from '@mui/material/colors';

const { DRAFT, PUBLISHED, ARCHIVED } = ContentStatus;
type Params = {
  organizationId: string;
  contentStatus: 'publicados' | 'borradores' | 'archivados';
  contentId?: string;
};

const OrganizationContent = () => {
  const navigate = useNavigate();
  const { organizationId, contentStatus, contentId } = useParams<Params>();
  const [activeDrawer, setActiveDrawer] = useState<'dish' | 'offer' | null>(
    null
  );
  const { hasAccess } = useAccessControl();
  const users = useAppSelector((state) => state.users);
  const { data = [] as Content[], isLoading } = useFetchContentQuery(
    organizationId
  ) || { data: [] };

  const categories = useAppSelector((state) => state.categories);
  const orgCat = useAppSelector(
    (state) => state.organizations[organizationId ?? '']?.categories
  );

  const selectedContent = useMemo(() => {
    return (data ?? []).find(({ id }: { id: string }) => id === contentId);
  }, [contentId, data]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        flex: 1,
        field: 'title',
        headerName: 'ID',
        disableColumnMenu: true,
      },
      {
        flex: 1,
        field: 'type',
        headerName: 'Tipo',
        disableColumnMenu: true,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params: GridValueGetterParams<Content>) => {
          const isOffer = params.row.type === 'offer';

          return (
            <Chip
              size="small"
              label={isOffer ? 'Oferta' : 'Plato'}
              sx={{
                backgroundColor: isOffer ? red[100] : lightGreen[100]
              }}
            />
          );
        },
      },
      {
        flex: 1,
        field: 'createdBy',
        headerName: 'Creada Por',
        disableColumnMenu: true,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params: GridValueGetterParams<Content>) => {
          return (
            <Avatar sx={{ width: 32, height: 32, backgroundColor: ({palette}) => palette.primary.main }} >
              <Typography variant="body1">{getInitials(params.row.createdBy)}</Typography>
            </Avatar>
          );
        },
      },
      {
        flex: 1,
        field: 'category',
        headerName: 'Categoría',
        valueGetter: (params: GridValueGetterParams<Content>) => {
          if (params.row.type === 'offer') return 'Oferta';

          const draft = orgCat?.filter((c: { id: string }) => {
            if (c.id === params.row.category) return c;
          });
          return (
            categories[params.row.category]?.title ??
            draft?.map((c: { title: string }) => c.title)
          );
        },
      },
    ],
    [categories, orgCat]
  );

  const closeDrawer = () => {
    setActiveDrawer(null);
    navigate(
      generatePath(routes.organizations.content.main, {
        organizationId,
        contentStatus,
      })
    );
  };

  const contentByStatus = useMemo(() => {
    const organizationContent = (data ?? []).map((content: Content) => {
      const user = users[content.createdBy];
      return {
        ...content,
        createdBy: user ? user.name : '',
      };
    });

    switch (contentStatus) {
      case 'publicados':
        return organizationContent.filter(
          ({ status }: { status: ContentStatus }) => status === PUBLISHED
        );
      case 'archivados':
        return organizationContent.filter(
          ({ status }: { status: ContentStatus }) => status === ARCHIVED
        );
      case 'borradores':
        return organizationContent.filter(
          ({ status }: { status: ContentStatus }) => status === DRAFT
        );
      default:
        return [];
    }
  }, [contentStatus, data, users]);

  const actions = useMemo(() => {
    const draft = [];

    if (hasAccess('create.content')) {
      draft.push({
        children: 'Crear Plato',
        color: 'success',
        onClick: () => setActiveDrawer('dish'),
      });
    }

    if (hasAccess('create.content')) {
      draft.push({
        children: 'Crear Oferta',
        color: 'success',
        onClick: () => setActiveDrawer('offer'),
      });
    }

    return draft as ButtonProps[];
  }, [hasAccess]);

  if (isLoading) {
    return (
      <Stack flex={1} alignContent="center" justifyContent="center" padding={3}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack spacing={2} pb={3}>
      <PageHeader
        title="Contenido"
        action={
          hasAccess('create.content') ? (
            <ButtonGroup actions={actions} />
          ) : undefined
        }
      />
      <ContentTabs />
      <Paper>
        <DataGrid
          hideFooter={false}
          columns={columns}
          rows={contentByStatus}
          onRowClick={(params: GridRowParams<Content | Offer>) => {
            setActiveDrawer(params.row.type ? params.row.type : 'dish');
            navigate(
              generatePath(routes.organizations.content.details, {
                organizationId,
                contentStatus,
                contentId: params.row.id,
              })
            );
          }}
        />
        <ContentDrawer
          open={activeDrawer === 'dish' || selectedContent?.type === 'dish'}
          selectedContent={selectedContent}
          onClose={closeDrawer}
        />

        <OfferDrawer
          open={activeDrawer === 'offer' || selectedContent?.type === 'offer'}
          selectedContent={selectedContent}
          onClose={closeDrawer}
        />
      </Paper>
    </Stack>
  );
};

export default OrganizationContent;
